/**
 * Money input editing rules — plain text while focused, formatted display on blur.
 * Tested in money-input.test.mjs (mirrors these exports).
 */
import { formatMoneyInputBlur, parseMoneyInput } from './formatters'

/** Strip currency decoration for in-progress editing. */
export function moneyDraftFromDisplay(display: string): string {
  return String(display ?? '').replace(/[$,\s]/g, '')
}

/** Thousands separators while typing (draft has no $ or commas). */
export function formatMoneyTypingDraft(draft: string): string {
  const raw = String(draft ?? '')
  if (!raw) return ''
  const dot = raw.indexOf('.')
  const intPart = (dot === -1 ? raw : raw.slice(0, dot)).replace(/\D/g, '')
  const decPart = dot === -1 ? '' : raw.slice(dot + 1).replace(/\D/g, '')
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  if (dot !== -1) {
    if (raw.endsWith('.') && !decPart) return `${intFormatted || '0'}.`
    return `${intFormatted || '0'}.${decPart}`
  }
  return intFormatted
}

/** Keep digits and at most one decimal point. */
export function sanitizeMoneyDraft(raw: string): string {
  let cleaned = String(raw ?? '').replace(/[^0-9.]/g, '')
  const firstDot = cleaned.indexOf('.')
  if (firstDot !== -1) {
    cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
  }
  return cleaned
}

/** Count raw digit/dot positions before a caret in a formatted money display. */
export function rawIndexFromDisplay(formatted: string, displayCaret: number): number {
  let n = 0
  const end = Math.max(0, Math.min(displayCaret, formatted.length))
  for (let i = 0; i < end; i++) {
    const ch = formatted[i]
    if (ch >= '0' && ch <= '9' || ch === '.') n++
  }
  return n
}

/** Map a raw digit/dot index to a caret position in the formatted typing display. */
export function caretPosInFormattedDraft(draft: string, rawIndex: number): number {
  const shown = formatMoneyTypingDraft(draft)
  if (rawIndex <= 0) return 0
  let n = 0
  for (let i = 0; i < shown.length; i++) {
    const ch = shown[i]
    if (ch >= '0' && ch <= '9' || ch === '.') {
      n++
      if (n === rawIndex) return i + 1
    }
  }
  return shown.length
}

/** Sanitize while preserving caret position for mid-string edits. */
export function sanitizeMoneyDraftWithCaret(
  raw: string,
  caret: number,
): { value: string; caret: number } {
  const pos = Math.max(0, Math.min(caret, raw.length))
  const before = raw.slice(0, pos).replace(/[^0-9.]/g, '')
  const after = raw.slice(pos).replace(/[^0-9.]/g, '')
  let merged = before + after
  const dotInBefore = before.includes('.')
  if (dotInBefore) {
    merged = before + after.replace(/\./g, '')
  } else {
    const firstDot = merged.indexOf('.')
    if (firstDot !== -1) {
      merged = merged.slice(0, firstDot + 1) + merged.slice(firstDot + 1).replace(/\./g, '')
    }
  }
  const nextCaret = Math.min(before.length, merged.length)
  return { value: merged, caret: nextCaret }
}

/** Apply a formatted display edit; returns next draft and display caret. */
export function applyMoneyDisplayEdit(
  _prevDraft: string,
  displayValue: string,
  displayCaret: number,
): { draft: string; displayCaret: number } {
  const rawCaretInNew = rawIndexFromDisplay(displayValue, displayCaret)
  const { value: nextDraft, caret: nextRawCaret } = sanitizeMoneyDraftWithCaret(
    moneyDraftFromDisplay(displayValue),
    rawCaretInNew,
  )
  return {
    draft: nextDraft,
    displayCaret: caretPosInFormattedDraft(nextDraft, nextRawCaret),
  }
}

/** Format draft for display on blur (no $ prefix — field context may add it). */
export function formatMoneyBlurValue(raw: string): string {
  const t = String(raw ?? '').trim()
  if (!t) return ''
  return formatMoneyInputBlur(t)
}

export function parseMoneyBlurValue(raw: string): number {
  return parseMoneyInput(raw)
}

export type MoneyInputEditState = {
  focused: boolean
  display: string
  draft: string
}

export function moneyInputFocusState(display: string): MoneyInputEditState {
  return {
    focused: true,
    display,
    draft: moneyDraftFromDisplay(display),
  }
}

export function moneyInputChangeState(
  state: MoneyInputEditState,
  raw: string,
  caret: number,
): MoneyInputEditState {
  const { value, caret: _ } = sanitizeMoneyDraftWithCaret(raw, caret)
  return { ...state, focused: true, draft: value }
}

export function moneyInputBlurState(
  state: MoneyInputEditState,
  formatBlur: (raw: string) => string = formatMoneyBlurValue,
): MoneyInputEditState {
  const display = state.draft.trim() === '' ? '' : formatBlur(state.draft)
  return { focused: false, display, draft: '' }
}

/** Simulate typing a digit sequence from an empty focused field. */
export function simulateMoneyTyping(
  chars: string,
  formatBlur: (raw: string) => string = formatMoneyBlurValue,
): { draft: string; display: string; numeric: number } {
  let state = moneyInputFocusState('')
  for (const ch of chars) {
    const nextRaw = state.draft + ch
    state = moneyInputChangeState(state, nextRaw, nextRaw.length)
  }
  state = moneyInputBlurState(state, formatBlur)
  return {
    draft: state.draft,
    display: state.display,
    numeric: parseMoneyInput(state.display),
  }
}

/** Simulate inserting text at a caret inside a formatted display value. */
export function simulateMoneyMidEdit(
  display: string,
  insertAt: number,
  insertText: string,
  formatBlur: (raw: string) => string = formatMoneyBlurValue,
): { display: string; draft: string } {
  let state = moneyInputFocusState(display)
  const draft = state.draft
  const pos = Math.max(0, Math.min(insertAt, draft.length))
  const nextRaw = draft.slice(0, pos) + insertText + draft.slice(pos)
  state = moneyInputChangeState(state, nextRaw, pos + insertText.length)
  state = moneyInputBlurState(state, formatBlur)
  return { display: state.display, draft: state.draft }
}
